import { css } from 'aphrodite';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import {
	Dialog,
	DialogBody,
	DialogDescription,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../../web/components-v2/Dialog';
import { Button } from '../../../../web/components/Button';
import { usePreselectedScenario } from '../../../contexts/preselectedScenario';
import { useMyScenarios, useScenario } from '../../../queries/Scenarios';
import { ApiClient } from '../../../services/api';
import { VoiceAgentInterface as VoiceAgentInterfaceComponent } from '../../components/VoiceAgentInterface';
import { useTysonCoaching } from '../../contexts/TysonCoachingContext';
import { usePracticeCallMutations } from '../../hooks/usePracticeCallMutations';
import { useRepCoachingGoals } from '../../hooks/useRepCoachingGoals';
import { VoiceAgent } from '../../services/VoiceAgent';
import { buildSystemPrompt } from '../../services/VoiceAgentPromptService';
import { PitchType, Provider, TranscriptEntry, VoiceAgentModalProps, VoiceAgentStatus } from '../../types';
import { ScenarioSelector } from '../ScenarioSelector';
import { styleSheet } from './styles';

export const VoiceAgentModal: React.FC<VoiceAgentModalProps> = ({ isOpen, onClose, leadId, contact, company }) => {
	const [pitchType, setPitchType] = useState<PitchType>('cold-call');
	const [provider, _] = useState<Provider>('elevenlabs');
	const [status, setStatus] = useState<VoiceAgentStatus>({
		isConnected: false,
		isConnecting: false,
	});
	const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
	const [voiceAgent, setVoiceAgent] = useState<import('../../types').VoiceAgentInterface | null>(null);
	const [phoneCallId, setPhoneCallId] = useState<string | null>(null);
	const [selectedScenarioId, setSelectedScenarioId] = useState<string | null>(null);
	const { data: selectedScenario } = useScenario({ id: selectedScenarioId });
	const { openAI, elevenLabs } = usePracticeCallMutations();
	const userSession = useUserSession();
	const { data: repCoachingGoals } = useRepCoachingGoals(userSession.user.id);
	const coaching = useTysonCoaching();
	const autoSuggestionsEnabled = userSession.user?.userPreferences?.coffeePreferences?.autoSuggestionsEnabled ?? false;
	const { preselectedScenarioId, clearPreselectedScenarioId } = usePreselectedScenario();

	// Get all available scenarios for random selection
	const { data: allScenarios } = useMyScenarios();
	const scenariosArray = useMemo(() => (Array.isArray(allScenarios) ? allScenarios : []), [allScenarios]);

	const getRandomScenarioId = useCallback(() => {
		if (scenariosArray.length === 0) return null;
		const activeScenarios = scenariosArray.filter(s => s.active !== false);
		if (activeScenarios.length === 0) return null;
		const randomIndex = Math.floor(Math.random() * activeScenarios.length);
		return activeScenarios[randomIndex].id;
	}, [scenariosArray]);

	// Refs to access latest coaching state in async code
	const conversationIdRef = useRef(coaching.conversationId);
	const isAIRespondingRef = useRef(coaching.isAIResponding);

	// Keep refs in sync with coaching state
	useEffect(() => {
		conversationIdRef.current = coaching.conversationId;
		isAIRespondingRef.current = coaching.isAIResponding;
	}, [coaching.conversationId, coaching.isAIResponding]);

	// Reset state when modal opens
	useEffect(() => {
		if (isOpen) {
			setTranscript([]);
			setStatus({ isConnected: false, isConnecting: false });
			setPhoneCallId(null);
			// Use preselected scenario if provided, otherwise null
			setSelectedScenarioId(preselectedScenarioId || null);
			// Clear the preselected scenario from context so it's not reused
			if (preselectedScenarioId) {
				clearPreselectedScenarioId();
			}
			if (voiceAgent) {
				voiceAgent.disconnect();
				setVoiceAgent(null);
			}
		}
		// Note: We intentionally exclude preselectedScenarioId and clearPreselectedScenarioId from deps because:
		// - We only want this effect to run when isOpen changes
		// - The preselected value is read once when modal opens
		// - Including them would cause unwanted re-runs
	}, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			if (voiceAgent) {
				voiceAgent.disconnect();
			}
		};
	}, [voiceAgent]);

	const handleConnect = useCallback(async () => {
		try {
			setStatus({ isConnected: false, isConnecting: true });

			// If no scenario selected, pick a random one
			const scenarioIdToUse = selectedScenarioId || getRandomScenarioId();
			if (scenarioIdToUse && !selectedScenarioId) {
				setSelectedScenarioId(scenarioIdToUse);
			}

			// Look up scenario prompt directly from the already-loaded scenarios list
			// instead of relying on the useScenario hook, which may not have fetched yet
			// (e.g., when a random scenario was just selected above)
			const scenarioForPrompt = scenarioIdToUse
				? (scenariosArray.find(s => s.id === scenarioIdToUse) ?? selectedScenario)
				: selectedScenario;

			if (scenarioIdToUse && !scenarioForPrompt) {
				throw new Error('Scenario data not yet loaded. Please try again.');
			}

			// Safety check - validate required data (leadId is optional for practice calls)
			if (!company?.id || !contact?.id) {
				throw new Error(
					'Invalid state: Required data (company or contact) is missing. This is a bug - please report it.'
				);
			}

			// Clean up any existing connection
			if (voiceAgent) {
				voiceAgent.disconnect();
				setVoiceAgent(null);
			}

			// Step 1: Call the appropriate API to get tokens
			const mutation = provider === 'openai' ? openAI : elevenLabs;
			const response = await mutation.mutateAsync({
				companyId: company.id,
				leadServedId: leadId,
				contactId: contact.id,
				scenarioId: scenarioIdToUse || undefined,
			});

			// Extract data from response - handle different property names
			const {
				clientToken,
				leadAIContext,
				agentTemplate,
				externalConversationToken: externalConversationId,
				phoneCallId: responsePhoneCallId,
			} = response;
			const objectivePrompt = agentTemplate?.objectivePrompt || '';

			// Store phoneCallId for later use
			if (responsePhoneCallId) {
				setPhoneCallId(responsePhoneCallId);
			}

			// Step 2: Ensure coaching session exists, reuse if already active (Queue auto-starts it)
			if (!coaching.isActive || !coaching.conversationId || coaching.isCreatingConversation) {
				// If creation is already in progress (e.g. Queue auto-start), wait for it
				// instead of starting a second concurrent creation
				if (!coaching.isCreatingConversation) {
					await coaching.startCoaching({
						companyId: company.id,
						leadServedId: leadId,
						contactId: contact.id,
						objectivePrompt,
						phoneCallId: responsePhoneCallId,
					});
				}

				// Wait for conversation to be ready before connecting voice agent
				let attempts = 0;
				const maxAttempts = 30; // 30 seconds max wait
				while ((!conversationIdRef.current || isAIRespondingRef.current) && attempts < maxAttempts) {
					await new Promise(resolve => setTimeout(resolve, 1000));
					attempts++;
				}

				if (!conversationIdRef.current) {
					throw new Error('Failed to create coaching conversation');
				}

				// Enrich with practice call phoneCallId if we waited for an existing creation
				if (responsePhoneCallId) {
					coaching.enrichCreationContext({ phoneCallId: responsePhoneCallId });
				}
			} else {
				// Coaching already active — enrich with practice call phoneCallId
				if (responsePhoneCallId) {
					coaching.enrichCreationContext({ phoneCallId: responsePhoneCallId });
				}
			}

			// Clear stale summary state from any previous call and notify coaching AI
			coaching.setCallEndedPendingSummary(false);
			coaching.clearPendingTranscriptData();
			const contactName = `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim() || 'Unknown Contact';
			coaching.sendSystemMessage(`Starting a new practice call with ${contactName}`);

			// Step 3: Build voice agent (sparring partner) prompt
			// Use the scenario prompt if a scenario is selected, otherwise fall back to pitchType-based prompts
			const scenarioPrompt = scenarioForPrompt?.prompt || '';
			const prompt = buildSystemPrompt(
				contact,
				scenarioPrompt,
				pitchType,
				leadAIContext,
				repCoachingGoals || { workingOn: '', tysonMaxDifficulty: 1 }
			);

			const apiToken = externalConversationId ?? clientToken;

			if (!clientToken) {
				throw new Error(
					`Missing client token for ${provider} provider. Backend API response: ${JSON.stringify(response)}`
				);
			}

			// Step 4: Create voice agent with transcript streaming callback
			const newAgent = new VoiceAgent({
				apiKey: apiToken,
				onTranscriptUpdate: setTranscript,
				onNewTranscriptEntry: entry => {
					// Send each new transcript entry to coaching
					coaching.sendTranscriptEntry(entry);
					// Auto-suggest when prospect finishes speaking, matching realtime calls behavior
					if (autoSuggestionsEnabled && entry.speaker !== 'Sales Rep' && !isAIRespondingRef.current) {
						coaching.requestSuggestion();
					}
				},
				provider,
			});

			// Step 5: Connect voice agent (voiceGender not used for ElevenLabs)
			await newAgent.connect(pitchType, prompt);
			setVoiceAgent(newAgent);
			setStatus({ isConnected: true, isConnecting: false });
			coaching.setIsCallConnected(true);

			// Update phoneCall with conversationId if both are available
			if (responsePhoneCallId) {
				const conversationId = newAgent.getConversationId();
				if (conversationId) {
					const apiClient = new ApiClient(userSession);
					try {
						await apiClient.updatePhoneCallConversationId(responsePhoneCallId, conversationId);
					} catch (error) {
						console.error('Failed to update phoneCall with conversationId:', error);
					}
				}
			}
		} catch (error) {
			// Connection failed
			setStatus({
				isConnected: false,
				isConnecting: false,
				error: {
					code: 'CONNECTION_FAILED',
					message: error instanceof Error ? error.message : 'Failed to connect to voice agent',
				},
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		pitchType,
		provider,
		voiceAgent,
		contact,
		company,
		leadId,
		openAI,
		elevenLabs,
		repCoachingGoals,
		coaching,
		userSession,
		selectedScenarioId,
		selectedScenario,
		scenariosArray,
		getRandomScenarioId,
	]);

	const handleDisconnect = useCallback(async () => {
		if (voiceAgent) {
			voiceAgent.disconnect();
			setVoiceAgent(null);
		}
		setStatus({ isConnected: false, isConnecting: false });
		coaching.setIsCallConnected(false);

		// Log full coaching conversation for easy copying
		if (coaching.coachingMessages.length > 0) {
			console.groupCollapsed('[Coffee] Full Conversation (%d messages)', coaching.coachingMessages.length);
			console.log(JSON.stringify(coaching.coachingMessages, null, 2));
			console.groupEnd();
		}

		// Show "Get Summary" button instead of auto-requesting summary
		coaching.setCallEndedPendingSummary(true);

		// Notify platform that practice call ended
		if (phoneCallId) {
			const apiClient = new ApiClient(userSession);
			try {
				await apiClient.notifyPracticeCallEnded(phoneCallId);
			} catch (error) {
				console.error('Failed to notify platform of practice call end:', error);
			}
		}
	}, [voiceAgent, coaching, phoneCallId, userSession]);

	const handleClose = useCallback(async () => {
		if (voiceAgent) {
			voiceAgent.disconnect();
			setVoiceAgent(null);
		}
		setStatus({ isConnected: false, isConnecting: false });
		coaching.stopCoaching();

		// Notify platform that practice call ended
		if (phoneCallId) {
			const apiClient = new ApiClient(userSession);
			try {
				await apiClient.notifyPracticeCallEnded(phoneCallId);
			} catch (error) {
				console.error('Failed to notify platform of practice call end:', error);
			}
		}

		onClose();
	}, [voiceAgent, onClose, coaching, phoneCallId, userSession]);

	const handleClearTranscript = useCallback(() => {
		if (voiceAgent) {
			voiceAgent.clearTranscript();
		}
		setTranscript([]);
	}, [voiceAgent]);

	const handleDownloadTranscript = useCallback(() => {
		if (!voiceAgent) return;

		const transcriptText = voiceAgent.getFormattedTranscript();
		if (!transcriptText) {
			alert('No transcript available to download');
			return;
		}

		const blob = new Blob([transcriptText], { type: 'text/plain' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `voice-agent-transcript-${new Date().toISOString().split('T')[0]}.txt`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	}, [voiceAgent]);

	return (
		<Dialog open={isOpen} onOpenChange={open => !open && handleClose()} dismissOnOutsideClick={false}>
			<DialogPopup styles={[styleSheet.dialogPopup]} overlay={false}>
				<DialogHeader>
					<DialogTitle>Voice Agent Practice Session</DialogTitle>
					<DialogDescription>Practice your sales call with an AI-powered prospect</DialogDescription>
				</DialogHeader>

				<DialogBody>
					{!status.isConnected && !status.isConnecting && (
						<div className={css(styleSheet.setupSection)}>
							{/* Hidden pitch type selector - functionality preserved but visually hidden */}
							<div className={css(styleSheet.pitchTypeSection)} style={{ display: 'none' }}>
								<label className={css(styleSheet.label)}>Pitch Type:</label>
								<select
									value={pitchType}
									onChange={e => setPitchType(e.target.value as PitchType)}
									className={css(styleSheet.select)}
								>
									<option value='cold-call'>Cold Call</option>
									<option value='product-demo'>Product Demo</option>
									<option value='follow-up'>Follow-up Call</option>
									<option value='objection-handling'>Objection Handling</option>
								</select>
							</div>

							<ScenarioSelector selectedScenarioId={selectedScenarioId} onSelectScenario={setSelectedScenarioId} />

							{status.error && <div className={css(styleSheet.error)}>{status.error.message}</div>}

							<div className={css(styleSheet.buttonSection)}>
								<Button
									kind='primary'
									onClick={handleConnect}
									disabled={status.isConnecting}
									className={css(styleSheet.connectButton)}
								>
									{status.isConnecting
										? coaching.isCreatingConversation
											? 'Preparing AI Coach...'
											: 'Connecting...'
										: 'Start Voice Session'}
								</Button>
							</div>
						</div>
					)}

					{(status.isConnected || status.isConnecting) && (
						<VoiceAgentInterfaceComponent
							isConnected={status.isConnected}
							isConnecting={status.isConnecting}
							transcript={transcript}
							onDisconnect={handleDisconnect}
							onClearTranscript={handleClearTranscript}
							onDownloadTranscript={handleDownloadTranscript}
							error={status.error}
						/>
					)}
				</DialogBody>
			</DialogPopup>
		</Dialog>
	);
};
